import InsightsIcon from '@mui/icons-material/Insights';
import { Stack, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useAppContext } from "../../../shared/contexts/app-context/components/AppContext";
import {
    usePortfolioSelection,
    usePortfolioManagement,
    usePortfolioProfile,
} from "../../../shared/contexts/portfolio-context/components/PortfolioContextProvider";
import { AssetType } from "../../../shared/types/AssetType";
import { assetService } from "../../../shared/services/AssetService";
import { blackScholesModelCalculationService } from "../../../shared/services/BlackScholesModelCalculationService";
import {
    portfolioCalculationService
} from "../../../shared/services/portfolio-calculation-service/PortfolioCalculationService";
import { LocalStorageConstants } from "../../../shared/constants/LocalStorageConstants";
import type { MarketDataDto } from "../../../shared/types/MarketDataDto";
import { ChangingDirectionType } from "../types";
import { StepChangeSelector } from "./StepChangeSelector";
import { ToolsPanelToggleButton } from "./ToolsPanelToggleButton";
import { colourChartStyle } from "../../portfolio-profile/components/charts/styles/ColourChartStyle";

const minCustomPrice = 1;
const maxCustomPrice = 999999;
const defaultCustomPriceStep = 100;
const customPriceStepOptions = [10000, 5000, 1000, 500, 100];

export function CustomPriceSelector() {
    const {
        fetchMarketData,
        marketDataDto,
        setMarketDataDto,
    } = useAppContext();

    const {
        selectedPortfolio,
    } = usePortfolioSelection();
    const {
        saveSelectedPortfolio,
    } = usePortfolioManagement();
    const {
        updateSelectedPortfolio,
        refreshPortfolioProfile,
    } = usePortfolioProfile();

    const [useCustomPrice, setUseCustomPrice] = useState<boolean>(false);
    const [customPrice, setCustomPrice] = useState('');
    const [customPriceStep, setCustomPriceStep] = useState(defaultCustomPriceStep);

    useEffect(() => {
        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        const settings = selectedPortfolio.portfolioSettings;

        setUseCustomPrice(settings.useCustomPrice);
        setCustomPrice(Math.round(settings.customPrice).toString());
        setCustomPriceStep(settings.customPriceStep || defaultCustomPriceStep);
    }, [selectedPortfolio, fetchMarketData]);

    const handleUseCustomPriceChanging = async (isChecked: boolean): Promise<void> => {
        setUseCustomPrice(isChecked)

        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        const settings = selectedPortfolio.portfolioSettings;

        settings.useCustomPrice = isChecked;
        await updateSelectedPortfolio();

        if (isChecked) {
            updateAppState(isChecked, Number(customPrice));
        } else {
            const selectedBaseAssetSymbol = localStorage.getItem(LocalStorageConstants.SelectedBaseAssetSymbol);
            const selectedExpiration = localStorage.getItem(LocalStorageConstants.SelectedExpirationName);

            fetchMarketData(selectedBaseAssetSymbol, selectedExpiration, (value: MarketDataDto | null): void => {
                if (value === null || value.UnderlyingPrice === null) {
                    return;
                }

                updateAppState(isChecked, value.UnderlyingPrice);
            });
        }
    }

    const updateAppState = (useCustomPrice: boolean, underlyingPrice: number): void => {
        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        const settings = selectedPortfolio.portfolioSettings;

        if (useCustomPrice) {
            settings.customPrice = Math.round(underlyingPrice);
        }

        if (settings.useOffset) {
            settings.minUnderlyingPrice = Math.round(underlyingPrice - settings.offset);
            settings.maxUnderlyingPrice = Math.round(underlyingPrice + settings.offset);
        }

        // update portfolio position parameters according to the new underlying price
        try {
            selectedPortfolio.orders.forEach(x => {
                if (x.type === AssetType.Futures || x.type === AssetType.Spot) {
                    x.markPrice = underlyingPrice;
                } else {
                    if (assetService.isOptionExpired(x))
                        return;

                    const timeToExpirationInShareOfYear = x.expireAt !== null
                        ? blackScholesModelCalculationService.getTimeToExpirationInShareOfYear(x.expireAt)
                        : null;

                    x.markPrice = portfolioCalculationService.balance.calculateOptionMarkPrice(x, underlyingPrice, timeToExpirationInShareOfYear!);
                    x.delta = portfolioCalculationService.delta.calculate(x, underlyingPrice, timeToExpirationInShareOfYear);
                    x.gamma = portfolioCalculationService.gamma.calculate(x, underlyingPrice, timeToExpirationInShareOfYear);
                    x.vega = portfolioCalculationService.vega.calculate(x, underlyingPrice, timeToExpirationInShareOfYear);
                    x.theta = portfolioCalculationService.theta.calculate(x, underlyingPrice, timeToExpirationInShareOfYear);
                }
            });
        }
        catch {
            // todo: show snackbar
        }

        updateSelectedPortfolio();
        saveSelectedPortfolio();

        if (marketDataDto !== undefined) {
            setMarketDataDto({
                ...marketDataDto,
                UnderlyingPrice: Math.round(underlyingPrice),
            });
            refreshPortfolioProfile(selectedPortfolio);
        }
    }

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (value === '' || /^\d{0,6}$/.test(value)) {
            setCustomPrice(value);
        }
    };

    const clampCustomPrice = (value: number): number => {
        if (value < minCustomPrice) return minCustomPrice;
        if (value > maxCustomPrice) return maxCustomPrice;

        return Math.round(value);
    };

    const onBlur = () => {
        if (customPrice === '' || customPrice == null) {
            setCustomPrice(Math.round(selectedPortfolio!.portfolioSettings!.customPrice).toString());
            return;
        }

        const num = clampCustomPrice(Number(customPrice));

        setCustomPrice(String(num));

        updateAppState(useCustomPrice, num);
    };

    const handleCustomPriceStepChange = (direction: ChangingDirectionType, step: number): void => {
        if (!useCustomPrice || selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        const currentValue = customPrice === ''
            ? selectedPortfolio.portfolioSettings.customPrice
            : Number(customPrice);

        const nextValue = clampCustomPrice(
            direction === ChangingDirectionType.Next
                ? currentValue + step
                : currentValue - step
        );

        setCustomPrice(String(nextValue));
        updateAppState(useCustomPrice, nextValue);
    };

    const handleCustomPriceStepSelectionChange = async (step: number): Promise<void> => {
        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.customPriceStep = step;
        await updateSelectedPortfolio();
        await saveSelectedPortfolio();
    };

    return (
        <Stack direction="row" spacing={0.5}>
            <ToolsPanelToggleButton
                isActivated={useCustomPrice}
                setIsActivated={(value) => {
                    const isChecked = typeof value === 'function'
                        ? value(useCustomPrice)
                        : value;

                    handleUseCustomPriceChanging(isChecked);
                }}
                tooltipTextWhenActivated="Use real underlying price"
                tooltipTextWhenDeactivated="Use custom underlying price"
            >
                <InsightsIcon
                    sx={{
                        height: '18px',
                        width: '18px',
                    }}
                />
            </ToolsPanelToggleButton>
            <TextField
                size="small"
                variant="standard"
                type="number"
                value={customPrice}
                onChange={onChange}
                onBlur={onBlur}
                disabled={!useCustomPrice}
                InputProps={{
                    disableUnderline: true,
                }}
                sx={{
                    borderColor: '#36454B',
                    borderRadius: '4px',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    height: '24px',
                    margin: '0px 0px 0px 0px',
                    padding: '2px 0px 0px 4px',
                    '&:before, &:after': {
                        borderBottom: 'none',
                    },
                    '&.MuiInput-underline.Mui-disabled:before': {
                        borderBottom: 'none',
                    },
                    '&:hover:not(.Mui-disabled, .Mui-error):before': {
                        borderBottom: 'none',
                    },
                    '& .MuiInputBase-root': {
                        width: '55px',
                        height: '24px',
                    },
                    '& .MuiInputBase-input': {
                        color: useCustomPrice ? colourChartStyle.selectedCustomLineColour : undefined,
                        WebkitTextFillColor: useCustomPrice ? colourChartStyle.selectedCustomLineColour : undefined,
                        fontSize: 15,
                        fontWeight: 'bold',
                    },
                    '& input': {
                        MozAppearance: 'textfield',
                    },
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                    },
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        onBlur();
                    }
                }}
            />
            <StepChangeSelector
                selectedStep={customPriceStep}
                setSelectedStep={setCustomPriceStep}
                stepOptions={customPriceStepOptions}
                disabled={!useCustomPrice || selectedPortfolio === null || selectedPortfolio.portfolioSettings === null}
                buttonContent="P"
                previousTooltipText="Reduce custom price"
                nextTooltipText="Increase custom price"
                onChange={handleCustomPriceStepChange}
                onStepSelectionChange={handleCustomPriceStepSelectionChange}
                selectorWidth ={75}
            />
        </Stack>
    )
}
